import { FormControl, Checkbox, TextField, Autocomplete, createFilterOptions } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { EmployeeUserType } from "../../types/employeeType";
import { employeesApi } from "../../api/employeesApi";
import { useTranslation } from "react-i18next";
import { notEmpty } from "../../common/logic/ListUtils";

interface Employee extends EmployeeUserType {
    fullName: string;
}

interface Props {
    value: string[];
    onChange: (value: string[]) => void;
}

export default function SelectEmployeesList(props: Props) {
    const { t } = useTranslation();
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [isLoadingEmployees, setIsLoadingEmployees] = useState<boolean>(false);
    const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);

    const loadEmployees = useCallback(async () => {
        setIsLoadingEmployees(true);
        const response = await employeesApi.getCompanyEmployees();
        if (response.success() && response.result) {
            setEmployees(response.result
                .map((e) => ({
                    ...e,
                    fullName: `${e.firstName} ${e.lastName}`
                }))
                .sort((a, b) => a.fullName.localeCompare(b.fullName, undefined, { sensitivity: "base" }))
            );
        }
        setIsLoadingEmployees(false);
    }, []);

    useEffect(() => {
        setSelectedEmployees(
            props.value
                .map(e => employees.find(em => em.id === e))
                .filter(notEmpty)
        );
    }, [props.value, employees])

    useEffect(() => {
        loadEmployees();
    }, []);

    const filterOptions = createFilterOptions<Employee>({
        stringify: (option) => option.fullName,
    });

    return <FormControl sx={{ m: 1, width: 300 }}>
        <Autocomplete
            multiple
            loading={isLoadingEmployees}
            options={employees}
            getOptionLabel={(option) => option.fullName}
            value={selectedEmployees}
            filterOptions={filterOptions}
            onChange={(_, value) => {
                setSelectedEmployees(value);
                props.onChange(value.map(e => e.id))
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label={t("label.employee")}
                />
            )}
            renderOption={(props, option, { selected }) => {
                return (
                    <li {...props} key={option.id}>
                        <Checkbox
                            style={{ marginRight: 8 }}
                            checked={selected}
                        />
                        {option.fullName}
                    </li>
                );
            }}
        />
    </FormControl>
}